import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../styles/PlanExtend.css';
import { COLORS } from '../styles/colors';
import { getMyPlan, extendPlanSubscription } from '../utils/api';
import { loadTossPayments } from '@tosspayments/payment-sdk';

const PlanExtend = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysLeft, setDaysLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [tossPayments, setTossPayments] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [extensionResult, setExtensionResult] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // 각 회사 규모별로 독립적인 상태 관리
  const [activePlanDurations, setActivePlanDurations] = useState({
    '소규모': '1개월 플랜',
    '중간 규모': '1개월 플랜',
    '대형': '1개월 플랜'
  });

  // 회사 규모 및 기간별 가격 정보
  const planPrices = {
    '소규모': {
      '1개월 플랜': 69000,
      '6개월 플랜': 390000,
      '12개월 플랜': 588000,
    },
    '중간 규모': {
      '1개월 플랜': 129000,
      '6개월 플랜': 690000,
      '12개월 플랜': 1188000,
    },
    '대형': {
      '1개월 플랜': 199000,
      '6개월 플랜': 1074000,
      '12개월 플랜': 1788000,
    }
  };

  // 플랜 기간 목록
  const planDurations = ['1개월 플랜', '6개월 플랜', '12개월 플랜'];

  // 회사 규모별 설명
  const sizeDescriptions = {
    '소규모': '10인 이하',
    '중간 규모': '11 - 50인',
    '대형': '51 - 100인'
  };

  // 할인 정보 계산
  const getDiscountInfo = (companySize, planDuration) => {
    if (planDuration === '1개월 플랜') {
      return { amount: 0, percentage: 0 };
    } else if (planDuration === '6개월 플랜') {
      // 6개월 플랜은 1개월 × 6의 가격에서 약 10% 할인된 가격
      const monthlyPrice = planPrices[companySize]['1개월 플랜'];
      const fullPrice = monthlyPrice * 6;
      const discountAmount = fullPrice - planPrices[companySize][planDuration];
      return { amount: discountAmount, percentage: 10 };
    } else if (planDuration === '12개월 플랜') {
      // 12개월 플랜은 1개월 × 12의 가격에서 약 25% 할인된 가격
      const monthlyPrice = planPrices[companySize]['1개월 플랜'];
      const fullPrice = monthlyPrice * 12;
      const discountAmount = fullPrice - planPrices[companySize][planDuration];
      return { amount: discountAmount, percentage: 25 };
    }
  };

  // 월별 가격 계산
  const getMonthlyPrice = (companySize, planDuration) => {
    const totalPrice = planPrices[companySize][planDuration];
    const months = planDuration === '1개월 플랜' ? 1 : planDuration === '6개월 플랜' ? 6 : 12;
    return Math.floor(totalPrice / months);
  };

  // 특정 회사 규모의 다음 플랜으로 이동
  const nextPlan = (companySize) => {
    const currentPlan = activePlanDurations[companySize];
    const currentIndex = planDurations.indexOf(currentPlan);
    const nextIndex = (currentIndex + 1) % planDurations.length;
    
    setActivePlanDurations(prev => ({
      ...prev,
      [companySize]: planDurations[nextIndex]
    }));
  };

  // 특정 회사 규모의 이전 플랜으로 이동
  const prevPlan = (companySize) => {
    const currentPlan = activePlanDurations[companySize];
    const currentIndex = planDurations.indexOf(currentPlan);
    const prevIndex = (currentIndex - 1 + planDurations.length) % planDurations.length;
    
    setActivePlanDurations(prev => ({
      ...prev,
      [companySize]: planDurations[prevIndex]
    }));
  };

  // 이미 처리된 결제인지 확인하는 함수
  const isPaymentAlreadyProcessed = (paymentKey) => {
    const processedPayments = JSON.parse(sessionStorage.getItem('processedPayments') || '[]');
    return processedPayments.includes(paymentKey);
  };
  
  // 처리된 결제 저장 함수
  const markPaymentAsProcessed = (paymentKey) => {
    const processedPayments = JSON.parse(sessionStorage.getItem('processedPayments') || '[]');
    if (!processedPayments.includes(paymentKey)) {
      processedPayments.push(paymentKey);
      sessionStorage.setItem('processedPayments', JSON.stringify(processedPayments));
    }
  };
  
  // URL 파라미터 확인
  useEffect(() => {
    // URL에서 searchParams 가져오기
    const searchParams = new URLSearchParams(location.search);
    const paymentKey = searchParams.get('paymentKey');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');
    
    // 모든 결제 정보가 있는지 확인하고, 이미 처리 중이 아닌 경우에만 처리
    // 또한 이미 처리된 paymentKey인지 확인
    if (paymentKey && orderId && amount && !processingPayment && !paymentSuccess && !isPaymentAlreadyProcessed(paymentKey)) {
      // 즉시 처리 플래그 설정 및 paymentKey 저장
      setProcessingPayment(true);
      markPaymentAsProcessed(paymentKey);
      
      // 브라우저 히스토리에서 URL 파라미터 제거 (중복 처리 방지)
      const url = new URL(window.location);
      url.searchParams.delete('paymentKey');
      url.searchParams.delete('orderId');
      url.searchParams.delete('amount');
      window.history.replaceState({}, '', url);
      
      // 결제 처리 실행
      handlePaymentSuccess(paymentKey, orderId, parseInt(amount));
    }
  }, [location, processingPayment, paymentSuccess]);
  
  // 결제 성공 처리 함수
  const handlePaymentSuccess = async (paymentKey, orderId, amount) => {
    try {
      setIsLoading(true);
      
      // 주문 ID에서 플랜 타입 추출
      const planType = getPlanTypeFromOrderId(orderId);
      
      // 구독 연장 API 호출
      const result = await extendPlanSubscription(planType, {
        paymentKey,
        orderId,
        amount
      });
      
      console.log('구독 연장 성공:', result);
      setPaymentSuccess(true);
      setExtensionResult(result);
      
      // 연장 성공 후 플랜 정보 다시 불러오기
      await fetchPlanData();
      
    } catch (error) {
      console.error('구독 연장 오류:', error);
      setError(error.message || '구독 연장 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
      setProcessingPayment(false); // 처리 완료 플래그 설정
    }
  };
  
  // 주문 ID에서 플랜 타입 추출
  const getPlanTypeFromOrderId = (orderId) => {
    try {
      // 주문 ID 형식: "tapgo-timestamp-random-planType"
      const parts = orderId.split('-');
      if (parts.length >= 4) {
        // 마지막 부분이 플랜 타입
        return parts[3];
      }
      
      // 기본값 반환
      return 'basic';
    } catch (error) {
      console.error('주문 ID 파싱 오류:', error);
      return 'basic';
    }
  };

  // 토스페이먼츠 SDK 초기화
  useEffect(() => {
    // 여기에 실제 클라이언트 키를 입력하세요
    const clientKey = 'test_ck_5OWRapdA8dbnkDdoKmWXVo1zEqZK';
    
    loadTossPayments(clientKey).then(tossPayments => {
      setTossPayments(tossPayments);
    });
  }, []);

  // 플랜 정보 가져오기
  useEffect(() => {
    fetchPlanData();
  }, []);
  
  // 플랜 정보 조회 함수
  const fetchPlanData = async () => {
    try {
      setLoading(true);
      const response = await getMyPlan();
      setPlanData(response.data);
      
      // 남은 일수 계산
      if (response.data && response.data.end_date) {
        const endDate = new Date(response.data.end_date);
        const today = new Date();
        const diffTime = Math.abs(endDate - today);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        setDaysLeft(diffDays);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('플랜 정보 로딩 오류:', err);
      setError('플랜 정보를 불러오는 중 오류가 발생했습니다.');
      setLoading(false);
    }
  };

  // 날짜 포맷 함수 (YYYY. MM. DD. 형식으로 변환)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}. ${month}. ${day}.`;
  };

  // 플랜 유형 매핑 (UI 플랜 -> API 플랜 타입)
  const planTypeMapping = {
    '1개월 플랜': 'basic',
    '6개월 플랜': 'premium',
    '12개월 플랜': 'enterprise'
  };

  const handleSubscribe = async (companySize) => {
    setIsLoading(true);
    
    try {
      const plan = activePlanDurations[companySize];
      console.log(`${plan} 구독 연장 신청`);
      
      // API 호출에 사용할 플랜 타입 변환
      const apiPlanType = planTypeMapping[plan];
      
      if (!tossPayments) {
        throw new Error('결제 모듈이 준비되지 않았습니다. 잠시 후 다시 시도해주세요.');
      }
      
      // 주문 ID 생성 (영문, 숫자, -, _만 포함하고 6-64자 이내로 제한)
      const orderId = `tapgo-${Date.now()}-${Math.floor(Math.random() * 1000)}-${apiPlanType}`;
      
      // 토스페이먼츠 결제창 열기
      await tossPayments.requestPayment('카드', {
        amount: planPrices[companySize][plan],
        orderId: orderId,
        orderName: `TapGo Plus ${plan} 구독 연장`,
        customerName: '고객',
        successUrl: `${window.location.origin}/dashboard/plan-extend`,
        failUrl: `${window.location.origin}/dashboard/plan-extend`,
      });
      
    } catch (error) {
      console.error('결제 실패:', error);
      alert(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // 결제 성공 후 표시할 컴포넌트
  const PaymentSuccessMessage = () => (
    <div className="paymentSuccessContainer">
      <div className="successIcon">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#FF6F0F"/>
        </svg>
      </div>
      <h2 className="successTitle">구독 연장이 완료되었습니다!</h2>
      <div className="successDetails">
        <p>플랜명: {extensionResult?.data?.plan_name}</p>
        <p>새로운 만료일: {formatDate(extensionResult?.data?.new_end_date)}</p>
      </div>
      <button 
        className="continueButton"
        onClick={() => {
          setPaymentSuccess(false);
          setExtensionResult(null);
        }}
      >
        계속하기
      </button>
    </div>
  );

  if (loading) {
    return <div className="planExtendContainer">로딩 중...</div>;
  }
  
  if (error) {
    // 토스페이먼츠 중복 요청 오류인 경우 특별한 메시지 표시
    if (error.includes('[S008]') || error.includes('기존 요청을 처리중입니다') || error.includes('이미 처리된 결제')) {
      return (
        <div className="planExtendContainer">
          <div className="infoMessage">
            <h3>결제가 처리 중이거나 이미 완료되었습니다</h3>
            <p>토스페이먼츠에서 결제가 처리되고 있거나 이미 완료되었습니다.</p>
            <p>페이지를 새로고침하시면 최신 구독 정보를 확인하실 수 있습니다.</p>
            <button 
              className="retryButton"
              onClick={() => window.location.reload()}
            >
              새로고침
            </button>
          </div>
        </div>
      );
    }
    
    // 일반적인 오류 메시지
    return (
      <div className="planExtendContainer">
        <div className="errorMessage">{error}</div>
        <button 
          className="retryButton"
          onClick={() => {
            // URL 파라미터 제거 후 페이지 새로고침
            const url = new URL(window.location);
            url.search = '';
            window.location.href = url.toString();
          }}
        >
          다시 시도
        </button>
      </div>
    );
  }
  
  // 결제 성공 메시지 표시
  if (paymentSuccess && extensionResult) {
    return <div className="planExtendContainer">
      <PaymentSuccessMessage />
    </div>;
  }

  return (
    <div className="planExtendContainer">
      <h1 className="pageTitle">나의플랜 &gt; 구독 연장</h1>
      
      <div className="planStatusInfo">
        <p>
          <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{'TapGo Plus'}</span> 플랜이 
          <span className="planExpiry"> {formatDate(planData?.end_date)}</span>에 종료됩니다. (
          <span className="daysLeft">{daysLeft}</span>일 남음)
        </p>      
      </div>

      <div className="planCards">
        {/* 소규모 */}
        <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
          <h2 className="planName">소규모</h2>
          <p className="sizeDescription">({sizeDescriptions['소규모']})</p>
          
          {activePlanDurations['소규모'] === '12개월 플랜' && (
            <div className="mostPopular">
              "가장 많이 선택한"
            </div>
          )}
          
          <div className="planCarousel">
            <button className="carouselButton left" onClick={() => prevPlan('소규모')}>
              &#10094;
            </button>
            
            <div className="planDuration">
              <h3>{activePlanDurations['소규모']}</h3>
          <div className="planPrice">
            <span className="pricePrefix">월</span>
                <span className="priceAmount">{getMonthlyPrice('소규모', activePlanDurations['소규모']).toLocaleString()}</span>
            <span className="priceSuffix">원</span>
          </div>
          
          <div className="planDetails">
            <div className="totalPrice">
              <span className="detailLabel">총 결제 금액</span>
                  <span className="detailValue">{planPrices['소규모'][activePlanDurations['소규모']].toLocaleString()} 원</span>
            </div>
            <div className="discountPrice">
                  <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                  <span className="detailValue" style={{ color: '#6C80E9' }}>
                    {getDiscountInfo('소규모', activePlanDurations['소규모']).amount > 0 
                      ? `${getDiscountInfo('소규모', activePlanDurations['소규모']).amount.toLocaleString()} 원 (${getDiscountInfo('소규모', activePlanDurations['소규모']).percentage}%)` 
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
            
            <button className="carouselButton right" onClick={() => nextPlan('소규모')}>
              &#10095;
            </button>
          </div>
          
          <button 
            className="subscribeButton"
            onClick={() => handleSubscribe('소규모')}
            style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
            disabled={isLoading}
          >
            <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
            <span className="buttonText">
              {isLoading ? '처리 중...' : 'TapGo Plus 구독연장하기'}
            </span>
          </button>
        </div>
        
        {/* 중간 규모 */}
        <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
          <h2 className="planName">중간 규모</h2>
          <p className="sizeDescription">({sizeDescriptions['중간 규모']})</p>
          
          {activePlanDurations['중간 규모'] === '12개월 플랜' && (
            <div className="mostPopular">
              "가장 많이 선택한"
            </div>
          )}
          
          <div className="planCarousel">
            <button className="carouselButton left" onClick={() => prevPlan('중간 규모')}>
              &#10094;
            </button>
            
            <div className="planDuration">
              <h3>{activePlanDurations['중간 규모']}</h3>
          <div className="planPrice">
            <span className="pricePrefix">월</span>
                <span className="priceAmount">{getMonthlyPrice('중간 규모', activePlanDurations['중간 규모']).toLocaleString()}</span>
            <span className="priceSuffix">원</span>
          </div>
          
          <div className="planDetails">
            <div className="totalPrice">
              <span className="detailLabel">총 결제 금액</span>
                  <span className="detailValue">{planPrices['중간 규모'][activePlanDurations['중간 규모']].toLocaleString()} 원</span>
            </div>
            <div className="discountPrice">
                  <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                  <span className="detailValue" style={{ color: '#6C80E9' }}>
                    {getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).amount > 0 
                      ? `${getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).amount.toLocaleString()} 원 (${getDiscountInfo('중간 규모', activePlanDurations['중간 규모']).percentage}%)` 
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
            
            <button className="carouselButton right" onClick={() => nextPlan('중간 규모')}>
              &#10095;
            </button>
          </div>
          
          <button 
            className="subscribeButton"
            onClick={() => handleSubscribe('중간 규모')}
            style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
            disabled={isLoading}
          >
            <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
            <span className="buttonText">
              {isLoading ? '처리 중...' : 'TapGo Plus 구독연장하기'}
            </span>
          </button>
        </div>
        
        {/* 대형 */}
        <div className="planCard" style={{ backgroundColor: COLORS.planCardBg, position: 'relative' }}>
          <h2 className="planName">대형</h2>
          <p className="sizeDescription">({sizeDescriptions['대형']})</p>
          
          {activePlanDurations['대형'] === '12개월 플랜' && (
            <div className="mostPopular">
            "가장 많이 선택한"
          </div>
          )}
          
          <div className="planCarousel">
            <button className="carouselButton left" onClick={() => prevPlan('대형')}>
              &#10094;
            </button>
            
            <div className="planDuration">
              <h3>{activePlanDurations['대형']}</h3>
          <div className="planPrice">
            <span className="pricePrefix">월</span>
                <span className="priceAmount">{getMonthlyPrice('대형', activePlanDurations['대형']).toLocaleString()}</span>
            <span className="priceSuffix">원</span>
          </div>
          
          <div className="planDetails">
            <div className="totalPrice">
              <span className="detailLabel">총 결제 금액</span>
                  <span className="detailValue">{planPrices['대형'][activePlanDurations['대형']].toLocaleString()} 원</span>
            </div>
            <div className="discountPrice">
                  <span className="detailLabel" style={{ color: '#6C80E9' }}>할인받은 금액</span>
                  <span className="detailValue" style={{ color: '#6C80E9' }}>
                    {getDiscountInfo('대형', activePlanDurations['대형']).amount > 0 
                      ? `${getDiscountInfo('대형', activePlanDurations['대형']).amount.toLocaleString()} 원 (${getDiscountInfo('대형', activePlanDurations['대형']).percentage}%)` 
                      : '-'}
                  </span>
                </div>
              </div>
            </div>
            
            <button className="carouselButton right" onClick={() => nextPlan('대형')}>
              &#10095;
            </button>
          </div>
          
          <button 
            className="subscribeButton"
            onClick={() => handleSubscribe('대형')}
            style={{ backgroundColor: COLORS.subscribeButtonBg, color: 'black' }}
            disabled={isLoading}
          >
            <span className="buttonPrefix smallText">출퇴근 관리를 쉽게!</span>
            <span className="buttonText">
              {isLoading ? '처리 중...' : 'TapGo Plus 구독연장하기'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanExtend; 